import { createFileRoute, Link } from "@tanstack/react-router";
import { SetupCopyBlock } from "@/components/setup/SetupCopyBlock";

export const Route = createFileRoute("/docs/mcp-tools")({
	component: McpTools,
});

const tools = [
	{
		category: "Workspace",
		items: [
			{
				name: "workspaceContext",
				description:
					"Return active workspace, available workspaces, current role, limits, timezone, subscription, and capability flags.",
				examplePrompt:
					"Show my active AgentReacher workspace and what capabilities are available.",
			},
			{
				name: "workspaceAction",
				description:
					"Create, switch, or delete a workspace when the user explicitly asks for workspace mutation.",
				examplePrompt:
					"Switch to the workspace I choose, then show its connected integrations.",
			},
		],
	},
	{
		category: "Integrations",
		items: [
			{
				name: "integrationList",
				description:
					"List connected social accounts/channels and the providers AgentReacher can connect.",
				examplePrompt:
					"List my connected social accounts and tell me what platforms are still available to connect.",
			},
			{
				name: "integrationConnect",
				description:
					"Start OAuth or secure setup for a provider. This is separate because it opens external authorization.",
				examplePrompt:
					"Start the connection flow for LinkedIn and give me the authorization action.",
			},
			{
				name: "integrationDisconnect",
				description:
					"Disconnect a social account from the active workspace. Pass channelId when multiple accounts exist for the same provider.",
				examplePrompt:
					"Show my connected accounts, then disconnect the account I choose.",
			},
			{
				name: "integrationAction",
				description:
					"Run provider-specific helper actions, such as listing or creating Pinterest boards.",
				examplePrompt:
					"List my Pinterest boards so I can choose where this pin should go.",
			},
		],
	},
	{
		category: "Posts",
		items: [
			{
				name: "postSchema",
				description:
					"Return platform requirements, limits, supported config fields, missing settings, and validation hints for selected targets and content type.",
				examplePrompt:
					"Check what fields are required for an image post to LinkedIn and Pinterest.",
			},
			{
				name: "postCompose",
				description:
					"Create or revise a draft or scheduled post. Supplying postId revises the existing planned post; omitting it creates one.",
				examplePrompt:
					"Create a scheduled post with this approved caption and selected media asset.",
			},
			{
				name: "postPlan",
				description:
					"List, inspect, revise, cancel, or delete planned posts without exposing separate CRUD tools.",
				examplePrompt:
					"List my scheduled posts for review, then delete the one I choose.",
			},
			{
				name: "postPublish",
				description:
					"Publish now, resume a scheduled post, or retry failed targets. Kept separate because it has external side effects.",
				examplePrompt:
					"Publish the approved post now and show me the delivery result.",
			},
			{
				name: "postStatus",
				description:
					"Read-only post and delivery inspection for one post, one delivery, or recent posts.",
				examplePrompt: "Check the delivery status for my latest post.",
			},
			{
				name: "mediaPrepare",
				description:
					"Import or prepare media from URL, base64 bytes, or direct-upload metadata for posting or the library. It does not generate media.",
				examplePrompt:
					"Prepare this image URL for use as post media and return the asset ID.",
			},
		],
	},
	{
		category: "Team",
		items: [
			{
				name: "teamAction",
				description:
					"List workspace members and pending invitations, or invite a member to the active workspace.",
				examplePrompt:
					"Invite this email address as a member of the active workspace.",
			},
		],
	},
	{
		category: "Feedback",
		items: [
			{
				name: "feedbackSend",
				description: "Send product feedback directly to the AgentReacher team.",
				examplePrompt:
					"Send this feedback to the AgentReacher team as an idea.",
			},
		],
	},
];

function McpTools() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<h1 className="text-2xl font-medium tracking-tight">
					MCP tools reference
				</h1>
				<p className="text-sm text-muted-foreground">
					All {tools.reduce((sum, cat) => sum + cat.items.length, 0)} tools
					available through Model Context Protocol.
				</p>
			</div>

			<div className="space-y-8">
				{tools.map((category) => (
					<section key={category.category} className="space-y-3">
						<h2 className="text-base font-medium text-foreground">
							{category.category}
						</h2>
						<div className="space-y-4">
							{category.items.map((tool) => (
								<div key={tool.name} className="space-y-1">
									<p>
										<code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono font-medium text-foreground">
											{tool.name}
										</code>
									</p>
									<p className="text-sm text-muted-foreground leading-relaxed">
										{tool.description}
									</p>
									<SetupCopyBlock text={tool.examplePrompt} multiline />
								</div>
							))}
						</div>
					</section>
				))}
			</div>

			<div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
				<p>
					Each tool accepts structured parameters, but you do not need to know
					the exact format. Your AI assistant handles the mapping. Just describe
					what you want in plain language.
				</p>
				<p>
					For setup instructions, see{" "}
					<Link
						to="/docs/mcp"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						MCP overview
					</Link>
					.
				</p>
			</div>

			<div className="flex items-center gap-4 text-sm">
				<Link
					to="/docs/mcp"
					className="text-muted-foreground hover:text-foreground transition-colors"
				>
					&larr; MCP overview
				</Link>
			</div>
		</article>
	);
}
